import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { formatDurationToHHMMSS } from '@lib/utils/units';
import { Tooltip } from '@mui/material';
import { videoPlayerStore, VideoPlayerStore } from '@stores/VideoPlayer';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VideoPlayerRef } from '../../../VideoPlayer/VideoPlayer';
import { pixelAtTime } from '../../timelineGeometry';
import css from './MomentHead.module.scss';
import { trimHandleTime, visibleTimeSpan } from './trimBounds';

type MomentHeadProps = {
  timelineWidth: number; // Total timeline width (including zoom)
  visibleContainerWidth: number; // Visible container width
  videoPlayerRef: React.RefObject<VideoPlayerRef | null>;
  scrollableContainerRef?: React.RefObject<any>; // Grid ref
  scrollLeft: number; // Current scroll position
  // Absolute timeline information
  timelineStartTime: number; // Absolute start time of the timeline (usually 0)
  timelineDuration: number; // Total duration of the timeline
  store?: VideoPlayerStore;
  // Zoom control functions
  onZoomChange?: (zoomLevel: number) => void;
  onCenterOnMoment?: () => void;
  // Declarative centering
  onRequestCenterOnTime?: (time: number) => void;
};

export const MomentHead = observer((props: MomentHeadProps) => {
  const {
    timelineWidth,
    visibleContainerWidth,
    videoPlayerRef,
    scrollableContainerRef,
    scrollLeft,
    timelineStartTime,
    timelineDuration,
    store = videoPlayerStore,
    onZoomChange,
    onCenterOnMoment,
    onRequestCenterOnTime,
  } = props;
  const isMobile = useIsMobile();
  const moment = store.moment;
  const [isDragging, setIsDragging] = useState<'start' | 'end' | null>(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartTime, setDragStartTime] = useState(0);
  const [dragStartScrollLeft, setDragStartScrollLeft] = useState(0);
  const [currentDragTime, setCurrentDragTime] = useState<number | null>(null);
  const scrollAnimationRef = useRef<number | undefined>(undefined);
  const momentHeadRef = useRef<HTMLDivElement>(null);
  const lastUpdateTimeRef = useRef<number>(0);
  const pendingUpdateRef = useRef<any>(null);
  const styleUpdateRef = useRef<number | null>(null);

  // No `if (!moment) return null` here. It used to sit above this line, ahead of every hook
  // below, which was inert only for as long as `moment` was guaranteed non-null for this
  // component's whole lifetime — true while the search result seeded it before mount and
  // nothing ever cleared it. A control that clears the selection while the timeline is on
  // screen makes null-while-mounted reachable, and React throws ("rendered more hooks than
  // during the previous render") rather than rendering nothing. The guard is now at the
  // return; every hook between here and there tolerates a null `moment` on its own.
  const handleMouseDown = useCallback(
    (e: React.MouseEvent, handle: 'start' | 'end') => {
      if (!moment) return;
      e.preventDefault();
      e.stopPropagation();

      setIsDragging(handle);
      store.setIsDragging('trim-handle', true);
      setDragStartX(e.clientX);
      setDragStartTime(handle === 'start' ? moment.start : moment.end);
      setCurrentDragTime(handle === 'start' ? moment.start : moment.end);
      setDragStartScrollLeft(scrollLeft);
    },
    [moment?.start, moment?.end, scrollLeft, store],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent, handle: 'start' | 'end') => {
      if (!isMobile || !moment) return;
      e.preventDefault();
      e.stopPropagation();

      const touch = e.touches[0];

      // When touch targets overlap, use whichever handle is closer
      let resolvedHandle = handle;
      if (momentHeadRef.current) {
        const rect = momentHeadRef.current.getBoundingClientRect();
        const midpoint = rect.left + rect.width / 2;
        resolvedHandle = touch.clientX < midpoint ? 'start' : 'end';
      }

      setIsDragging(resolvedHandle);
      store.setIsDragging('trim-handle', true);
      setDragStartX(touch.clientX);
      setDragStartTime(resolvedHandle === 'start' ? moment.start : moment.end);
      setCurrentDragTime(resolvedHandle === 'start' ? moment.start : moment.end);
      setDragStartScrollLeft(scrollLeft);
    },
    [isMobile, moment?.start, moment?.end, scrollLeft, store],
  );

  const performAutoScroll = useCallback(
    (mouseX: number) => {
      if (!scrollableContainerRef?.current?.element) return;

      const scrollContainer = scrollableContainerRef.current.element;
      const containerRect = scrollContainer.getBoundingClientRect();
      const scrollEdgeThreshold = 50;
      const maxScrollSpeed = 8;

      let scrollSpeed = 0;

      // Check if cursor is near left edge
      if (mouseX < containerRect.left + scrollEdgeThreshold) {
        const distance = Math.max(0, containerRect.left + scrollEdgeThreshold - mouseX);
        scrollSpeed = -Math.min(maxScrollSpeed, (distance / scrollEdgeThreshold) * maxScrollSpeed);
      }
      // Check if cursor is near right edge
      else if (mouseX > containerRect.right - scrollEdgeThreshold) {
        const distance = Math.max(0, mouseX - (containerRect.right - scrollEdgeThreshold));
        scrollSpeed = Math.min(maxScrollSpeed, (distance / scrollEdgeThreshold) * maxScrollSpeed);
      }

      if (scrollSpeed !== 0) {
        const currentScrollLeft = scrollContainer.scrollLeft;
        const newScrollLeft = Math.max(
          0,
          Math.min(
            scrollContainer.scrollWidth - scrollContainer.clientWidth,
            currentScrollLeft + scrollSpeed,
          ),
        );

        scrollContainer.scrollLeft = newScrollLeft;

        // Continue scrolling
        scrollAnimationRef.current = requestAnimationFrame(() => performAutoScroll(mouseX));
      }
    },
    [scrollableContainerRef],
  );

  // Calculate minimum duration based on zoom level to ensure 36px visual width
  const calculateMinimumDuration = useCallback(() => {
    if (!timelineWidth || !timelineDuration) return 0.1; // Fallback to 0.1 seconds

    // Calculate pixels per second at current zoom level
    const pixelsPerSecond = timelineWidth / timelineDuration;

    // Calculate minimum duration for 36px visual width
    const minDurationSeconds = 36 / pixelsPerSecond;

    return Math.max(0.1, minDurationSeconds); // Ensure minimum of 0.1 seconds
  }, [timelineWidth, timelineDuration]);

  // Calculate the current visual width of the moment head in pixels
  const momentVisualWidth = useMemo(() => {
    if (!moment || !timelineWidth || !timelineDuration) return 0;

    const momentDuration = moment.end - moment.start;
    const pixelsPerSecond = timelineWidth / timelineDuration;

    return momentDuration * pixelsPerSecond;
  }, [moment, timelineWidth, timelineDuration]);

  // Check if the moment head is too small (less than 36px)
  // But only show zoom button when NOT actively dragging
  const isMomentTooSmall = useMemo(() => {
    return momentVisualWidth < 36 && !isDragging;
  }, [momentVisualWidth, isDragging]);

  // Calculate the required zoom level to make the moment head at least 36px
  const calculateRequiredZoomLevel = useCallback(() => {
    if (!moment || !visibleContainerWidth || !timelineDuration) return 100;

    const momentDuration = moment.end - moment.start;

    // Calculate the zoom level needed to make the moment take up at least 36px
    // We want: momentDuration * (zoomLevel/100) * containerWidth / timelineDuration >= 36
    // Solving for zoomLevel: zoomLevel >= (36 * timelineDuration) / (momentDuration * containerWidth) * 100
    const requiredZoom = ((36 * timelineDuration) / (momentDuration * visibleContainerWidth)) * 100;

    // Add some padding (20%) to ensure it's comfortably above 36px
    return Math.ceil(requiredZoom * 1.2);
  }, [moment, visibleContainerWidth, timelineDuration]);

  // Handle auto-zoom button click
  const handleAutoZoomClick = useCallback(() => {
    if (!onZoomChange || !moment) return;

    // Check if current timestamp is within the moment
    const currentTime = store.currentTime;
    const isTimestampWithinMoment = currentTime >= moment.start && currentTime <= moment.end;

    // If timestamp is not within the moment, move it to the beginning of the moment
    if (!isTimestampWithinMoment) {
      videoPlayerRef.current?.scrubTo(moment.start);
    }

    const requiredZoom = calculateRequiredZoomLevel();
    const currentZoomLevel = (timelineWidth / visibleContainerWidth) * 100;

    if (requiredZoom > currentZoomLevel) {
      // To avoid a jarring UX, only zoom in. Do not zoom out.
      // This ensures users can always make moments larger for editing, but prevents
      // accidental zoom-out that could make already-editable moments too small.
      onZoomChange(requiredZoom);
      // Request centering on the moment's center time after zoom
      if (onRequestCenterOnTime) {
        onRequestCenterOnTime(currentTime);
      }
    }
  }, [
    onZoomChange,
    onRequestCenterOnTime,
    calculateRequiredZoomLevel,
    store.currentTime,
    moment?.start,
    moment?.end,
    videoPlayerRef,
    visibleContainerWidth,
    timelineDuration,
    momentVisualWidth,
    isMomentTooSmall,
  ]);

  // Handle click on moment head border area (not on handles)
  const handleMomentHeadClick = useCallback(
    (e: React.MouseEvent) => {
      // Only trigger zoom if we're not clicking on a handle (works for both small and large moments)
      const target = e.target as HTMLElement;
      const isHandle = target.closest(`.${css.handle}`);

      if (!isHandle && isMomentTooSmall) {
        // Only trigger auto-zoom if the moment is too small for easy interaction.
        // This prevents accidental zooming when clicking on large moments that are already editable.
        handleAutoZoomClick();
      }
    },
    [handleAutoZoomClick, isMomentTooSmall],
  );

  // Throttled update function to prevent too many state updates
  const updateMomentPosition = useCallback(
    (newTime: number, handle: 'start' | 'end') => {
      if (pendingUpdateRef.current) {
        cancelAnimationFrame(pendingUpdateRef.current);
        pendingUpdateRef.current = null;
      }

      pendingUpdateRef.current = requestAnimationFrame(() => {
        pendingUpdateRef.current = null;
        // The frame lands a tick after the pointer event that asked for it, and clearing the
        // selection while a handle is held is reachable (`SelectionBar`'s Clear). Read from the
        // store rather than the render's `moment` snapshot: the snapshot was non-null when the
        // frame was scheduled by construction, so testing it would never catch a clear that
        // happens in between — the alternative was a `!` that throws inside a frame callback,
        // where there is no handler to catch it.
        const current = store.moment;
        if (!current) return;

        // All three measurements off the scroll container itself, in one read, rather than
        // assembled from the `scrollLeft` state and the `timelineWidth` / `visibleContainerWidth`
        // props. Those are not from the same instant: `timelineWidth` is recomputed from the zoom
        // during render, while `scrollLeft` only catches up when the Grid's scroll event fires a
        // frame or more later — and the strip auto-zooms the moment it opens, by up to 100×.
        //
        // Dividing a scroll position by a width it was never measured against puts the window
        // wherever the ratio happens to land, and at 10000% zoom that is the top of the media:
        // 800px of viewport over 80,000px of strip is 27 seconds of a 45-minute file, so a
        // moment at 44:21 was clamped against a window of [0:00, 0:27]. `scrollLeft` and
        // `scrollWidth` read off one element cannot disagree that way — one is defined in terms
        // of the other.
        const strip = scrollableContainerRef?.current?.element;
        const bounds = {
          minDuration: calculateMinimumDuration(),
          mediaDuration: timelineDuration,
          visible: strip
            ? visibleTimeSpan(strip.scrollLeft, {
                timelineWidth: strip.scrollWidth,
                visibleWidth: strip.clientWidth,
                duration: timelineDuration,
              })
            : null,
        };
        const landed = trimHandleTime(handle, newTime, current, bounds);
        const newMoment =
          handle === 'start'
            ? { start: landed, end: current.end }
            : { start: current.start, end: landed };

        // The readout shows where the handle actually went, not where the pointer asked for.
        setCurrentDragTime(landed);

        store.setMoment(newMoment);

        // Seek to the handle's position for visual feedback. The end handle stops a hair short
        // of its own frame, which would otherwise be the first frame past the moment.
        videoPlayerRef.current?.scrubTo(handle === 'start' ? landed : landed - 0.001);
      });
    },
    [videoPlayerRef, store, scrollableContainerRef, timelineDuration, calculateMinimumDuration],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !scrollableContainerRef?.current || !moment) return;

      // Use requestAnimationFrame for smooth updates instead of throttling
      if (pendingUpdateRef.current) {
        cancelAnimationFrame(pendingUpdateRef.current);
      }

      pendingUpdateRef.current = requestAnimationFrame(() => {
        // Stop any existing scroll animation
        if (scrollAnimationRef.current) {
          cancelAnimationFrame(scrollAnimationRef.current);
        }

        // Get current scroll position
        const currentScrollLeft = scrollLeft;
        const scrollDelta = currentScrollLeft - dragStartScrollLeft;

        // Calculate the effective mouse position accounting for scroll
        const effectiveMouseX = e.clientX + scrollDelta;
        const deltaX = effectiveMouseX - dragStartX;

        const timelineDeltaPercent = (deltaX / timelineWidth) * 100;
        const duration = timelineDuration; // Use absolute timeline duration
        const timeDelta = (timelineDeltaPercent / 100) * duration;

        const newTime = Math.max(
          timelineStartTime,
          Math.min(timelineStartTime + duration, dragStartTime + timeDelta),
        );

        // Update moment position
        updateMomentPosition(newTime, isDragging);

        // Start auto-scrolling if needed
        performAutoScroll(e.clientX);

        pendingUpdateRef.current = null;
      });
    },
    [
      isDragging,
      dragStartX,
      dragStartTime,
      dragStartScrollLeft,
      timelineWidth,
      performAutoScroll,
      scrollableContainerRef,
      updateMomentPosition,
      timelineDuration,
      timelineStartTime,
      scrollLeft,
    ],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging || !scrollableContainerRef?.current || !moment) return;

      e.preventDefault();
      const touch = e.touches[0];

      if (pendingUpdateRef.current) {
        cancelAnimationFrame(pendingUpdateRef.current);
      }

      pendingUpdateRef.current = requestAnimationFrame(() => {
        if (scrollAnimationRef.current) {
          cancelAnimationFrame(scrollAnimationRef.current);
        }

        const currentScrollLeft = scrollLeft;
        const scrollDelta = currentScrollLeft - dragStartScrollLeft;
        const effectiveTouchX = touch.clientX + scrollDelta;
        const deltaX = effectiveTouchX - dragStartX;

        const timelineDeltaPercent = (deltaX / timelineWidth) * 100;
        const duration = timelineDuration;
        const timeDelta = (timelineDeltaPercent / 100) * duration;

        const newTime = Math.max(
          timelineStartTime,
          Math.min(timelineStartTime + duration, dragStartTime + timeDelta),
        );

        updateMomentPosition(newTime, isDragging);
        performAutoScroll(touch.clientX);

        pendingUpdateRef.current = null;
      });
    },
    [
      isDragging,
      dragStartX,
      dragStartTime,
      dragStartScrollLeft,
      timelineWidth,
      performAutoScroll,
      scrollableContainerRef,
      updateMomentPosition,
      timelineDuration,
      timelineStartTime,
      scrollLeft,
    ],
  );

  const handleMouseUp = useCallback(() => {
    // Stop any existing animations
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
    }

    if (pendingUpdateRef.current) {
      cancelAnimationFrame(pendingUpdateRef.current);
      pendingUpdateRef.current = null;
    }

    if (styleUpdateRef.current) {
      cancelAnimationFrame(styleUpdateRef.current);
      styleUpdateRef.current = null;
    }

    // Remember which handle was being dragged before clearing state
    const wasLeftHandle = isDragging === 'start';
    const wasRightHandle = isDragging === 'end';

    setIsDragging(null);
    store.setIsDragging('trim-handle', false);
    setDragStartX(0);
    setDragStartTime(0);
    setDragStartScrollLeft(0);
    setCurrentDragTime(null);

    // Seek back to the final handle position (in case video was playing during drag)
    if (store.mode === 'moment' && moment) {
      setTimeout(() => {
        if (wasLeftHandle) {
          // Seek back to start handle position
          videoPlayerRef.current?.scrubTo(moment.start);
        } else if (wasRightHandle) {
          // Seek back to end handle position
          videoPlayerRef.current?.scrubTo(moment.end - 0.001);
        }
      }, 100);
    }
  }, [moment, videoPlayerRef, isDragging, store]);

  // Add global mouse/touch event listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleMouseUp);
        if (scrollAnimationRef.current) {
          cancelAnimationFrame(scrollAnimationRef.current);
        }
        if (pendingUpdateRef.current) {
          cancelAnimationFrame(pendingUpdateRef.current);
        }
        if (styleUpdateRef.current) {
          cancelAnimationFrame(styleUpdateRef.current);
        }
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  // Clear the shared drag flag on unmount.
  //
  // `store` is sometimes the app-wide singleton (BaseMomentModal passes no
  // store), and only `handleMouseUp` clears this. Close the modal while a handle
  // is still held and that mouseup never arrives — the listener above is torn
  // down with the component — so the flag stays true for the rest of the
  // session. Anything that pauses work while the user is dragging would then
  // never start again.
  useEffect(() => {
    return () => {
      store.setIsDragging('trim-handle', false);
    };
  }, [store]);

  // Memoize positioning calculations to prevent unnecessary recalculations
  const positioning = useMemo(() => {
    if (!moment) {
      return {
        leftOverlayWidth: '0px',
        rightOverlayLeft: '100%',
        rightOverlayWidth: '0px',
        momentLeft: '0px',
        momentWidth: '0px',
      };
    }

    // Shared with the filmstrip cells and the needle. Spelling it out here is how
    // the mappings drifted apart before; it also skipped the zero-duration guard
    // that `pixelAtTime` carries, so a media with no duration produced NaN offsets.
    const geometry = { timelineWidth, duration: timelineDuration };
    const momentStartPx = pixelAtTime(moment.start, geometry);
    const momentEndPx = pixelAtTime(moment.end, geometry);

    // Calculate visible positions (accounting for scroll offset)
    const visibleMomentStart = Math.max(0, momentStartPx);
    const visibleMomentEnd = Math.max(0, momentEndPx);
    const visibleMomentWidth = Math.max(0, visibleMomentEnd - visibleMomentStart);

    // Calculate overlay positions
    const leftOverlayWidth = `${Math.max(0, momentStartPx)}px`;
    const rightOverlayLeft = `${Math.max(0, momentEndPx)}px`;
    // Fix right overlay width calculation to prevent overflow when moment extends beyond visible area
    const rightOverlayStart = Math.max(0, momentEndPx);
    const rightOverlayWidth = `${timelineWidth - momentEndPx}px`;

    const momentLeft = `${visibleMomentStart}px`;
    const momentWidth = `${visibleMomentWidth}px`;

    return {
      leftOverlayWidth,
      rightOverlayLeft,
      rightOverlayWidth,
      momentLeft,
      momentWidth,
    };
  }, [
    moment,
    timelineStartTime,
    timelineDuration,
    timelineWidth,
    visibleContainerWidth,
    scrollLeft,
  ]);

  const { leftOverlayWidth, rightOverlayLeft, rightOverlayWidth, momentLeft, momentWidth } =
    positioning;

  // Handle wheel events to forward them to the Grid
  const handleWheel = useCallback(
    (e: Event) => {
      const wheelEvent = e as WheelEvent;
      if (scrollableContainerRef?.current?.element) {
        // Forward the wheel event to the Grid's scroll container
        const gridElement = scrollableContainerRef.current.element;
        gridElement.scrollLeft += wheelEvent.deltaY;
        wheelEvent.preventDefault();
      }
    },
    [scrollableContainerRef],
  );

  // Add wheel event listener with passive: false to allow preventDefault
  useEffect(() => {
    const overlayElement = document.querySelector(`.${css.timelineOverlay}`);
    if (!overlayElement) return;

    overlayElement.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      overlayElement.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // Every hook is above this line — see the note where the old guard used to be.
  if (!moment) return null;

  return (
    <>
      {/* Overlay that covers the entire timeline */}
      <div className={css.timelineOverlay}>
        {/* Left overlay - from start to moment start */}
        <div
          className={css.overlaySection}
          style={{
            left: 0,
            transform: `translateX(0)`,
            width: leftOverlayWidth,
          }}
        />
        {/* Right overlay - from moment end to timeline end */}
        <div
          className={css.overlaySection}
          style={{
            left: 0,
            transform: `translateX(${rightOverlayLeft})`,
            width: rightOverlayWidth,
          }}
        />
      </div>

      {/* Original MomentHead */}
      <div
        ref={momentHeadRef}
        className={classNames(css.momentHead, {
          [css.defaultCursor]: !isMomentTooSmall,
          [css.mobileLayout]: isMobile,
        })}
        style={{
          left: momentLeft,
          width: momentWidth,
        }}
        onClick={handleMomentHeadClick}
      >
        {isMomentTooSmall ? (
          // Show auto-zoom button when moment is too small
          <Tooltip title="Click to zoom in and make the moment editable" arrow>
            <button
              className={css.autoZoomButton}
              onClick={(e) => {
                e.stopPropagation();
                handleAutoZoomClick();
              }}
              type="button"
            >
              <div className={css.autoZoomIcon}>🔍</div>
            </button>
          </Tooltip>
        ) : (
          // Show handles when moment is large enough
          <>
            <Tooltip title="Drag to adjust the start of the moment" arrow>
              <div
                className={classNames(css.handle, { [css.mobileHandle]: isMobile })}
                onMouseDown={(e) => handleMouseDown(e, 'start')}
                onTouchStart={(e) => handleTouchStart(e, 'start')}
              >
                <div className={css.innerHandle}></div>
              </div>
            </Tooltip>

            <Tooltip title="Drag to adjust the end of the moment" arrow>
              <div
                className={classNames(css.handle, { [css.mobileHandle]: isMobile })}
                onMouseDown={(e) => handleMouseDown(e, 'end')}
                onTouchStart={(e) => handleTouchStart(e, 'end')}
              >
                <div className={css.innerHandle}></div>
              </div>
            </Tooltip>
          </>
        )}

        <div className={css.durationPill}>
          <span>{Math.round(moment.end - moment.start)}s</span>
        </div>

        {!isMomentTooSmall && isDragging === 'start' && (
          <div className={css.leftHandleTimestamp}>{formatDurationToHHMMSS(currentDragTime!)}</div>
        )}
        {!isMomentTooSmall && isDragging === 'end' && (
          <div className={css.rightHandleTimestamp}>{formatDurationToHHMMSS(currentDragTime!)}</div>
        )}
      </div>
    </>
  );
});
